import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import NumberInputWithWords from '@/common/components/base/numberInputWithWords/NumberInputWithWords';
import FlowButton from '@/common/components/flowButton/FlowButton';
import useDebounce from '@/common/hooks/useDebounce';
import { getVehicleColorOption } from '@/common/queries/selections/vehicleColor/vehicleColorOption';
import { getVehicleProductYearOption } from '@/common/queries/selections/vehicleProductYear/vehicleProductYearOption';
import { InsuranceABV } from '@/common/types/SelectionOptions.type';
import { PUBLIC_REQUIRED_RULE } from '@/common/utils/rules/publicRules';
import { INITIAL_VISIT_LINKS } from '@/modules/adjusterReportModules/IncidentParticipants/pages/damaged/initialVisit/initialVisitLink';
import { getVehicleInitialInformationOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVehicleInitialInformation/getVehicleInitialInformationOption';
import { usePatchInitialVisitVehiclePartTwoQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchInitialVisitVehiclePartTwo/patchInitialVisitVehiclePartTwoQueryOptions';
import { VehicleInitialInformationPartTwoFormType } from '@/modules/adjusterReportModules/IncidentParticipants/types/VehicleInformationType';
import formatNumberWithCommas from '@/modules/adjusterReportModules/IncidentParticipants/utils/formatNumberWithCommas';
import { fileDetailQueryOption } from '@/modules/FileList/queries/fileDetail/fileDetailQueryOption';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Flex } from 'styled-system/jsx';

const InitialPartTwo = () => {
  const { id } = useParams();
  const { data: fileDetail } = useQuery(fileDetailQueryOption({ id: +id! }));
  const { data: vehicleProductsYears } = useQuery(
    getVehicleProductYearOption(),
  );
  const debounce = useDebounce();
  const [colorName, setColorName] = useState('');
  const navigate = useNavigate();
  const { data: vehicleColors } = useQuery({
    ...getVehicleColorOption({
      insurance: fileDetail?.insurance as InsuranceABV,
      name: colorName,
    }),
    enabled: !!fileDetail?.insurance,
  });
  const methods = useForm<VehicleInitialInformationPartTwoFormType>({
    mode: 'onChange',
  });

  const { data: vehicleInformation } = useQuery(
    getVehicleInitialInformationOption({
      id: +id!,
    }),
  );

  const vehicleTargetPart =
    vehicleInformation?.vehicle_type.name === 'motorcycle'
      ? 'شماره تنه'
      : 'شماره شاسی';

  const { mutate: patchPartTwo, isPending } =
    usePatchInitialVisitVehiclePartTwoQueryOptions();

  useEffect(() => {
    if (vehicleInformation) {
      methods.reset({
        vehicle_manufacturing_year:
          vehicleInformation.vehicle_manufacturing_year
            ? {
                label: vehicleInformation.vehicle_manufacturing_year,
                value: vehicleInformation.vehicle_manufacturing_year,
              }
            : null,
        vehicle_color: vehicleInformation.vehicle_color?.name
          ? {
              label: vehicleInformation.vehicle_color?.name,
              value: vehicleInformation.vehicle_color?.id,
            }
          : null,
        vehicle_vin: vehicleInformation.vehicle_vin,
        vehicle_chassis: vehicleInformation.vehicle_chassis,
        vehicle_engine: vehicleInformation.vehicle_engine,
        vehicle_price: formatNumberWithCommas(
          vehicleInformation.vehicle_price ?? '',
        ),
        vehicle_seat: vehicleInformation.vehicle_seat,
        vehicle_cylinder: vehicleInformation.vehicle_cylinder,
      });
      if (vehicleInformation.vehicle_manufacturing_year) {
        setTimeout(() => methods.trigger('vehicle_manufacturing_year'));
      }
    }
  }, [methods, vehicleInformation]);
  const submitHandler = (data: VehicleInitialInformationPartTwoFormType) => {
    patchPartTwo(
      {
        id: +id!,
        data: {
          vehicle_manufacturing_year:
            data.vehicle_manufacturing_year?.value ?? '',
          vehicle_color: Number(data.vehicle_color?.value) ?? 0,
          vehicle_vin: data.vehicle_vin ?? '',
          vehicle_chassis: data.vehicle_chassis ?? '',
          vehicle_engine: data.vehicle_engine ?? '',
          vehicle_price: Number(
            (data.vehicle_price ?? '0').toString().replace(/,/g, ''),
          ),
          vehicle_seat: data.vehicle_seat ?? 0,
          vehicle_cylinder: data.vehicle_cylinder ?? 0,
        },
      },
      {
        onSuccess: () => {
          navigate(-1);
        },
      },
    );
  };

  const handleColorOnChange = (value: string) => {
    if (value) {
      debounce(() => {
        setColorName(value);
      }, 1000);
    } else {
      setColorName(value);
    }
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submitHandler)}>
        <Flex direction="column" gap={1}>
          <ModalInput
            name="vehicle_manufacturing_year"
            label="سال ساخت"
            placeholder="انتخاب کنید"
            options={vehicleProductsYears?.map((item) => ({
              label: item.fa_const,
              value: item.const,
            }))}
            rules={{
              required: PUBLIC_REQUIRED_RULE,
            }}
          />

          <ModalInput
            name="vehicle_color"
            label="رنگ وسیله‌نقلیه"
            placeholder="انتخاب کنید"
            options={vehicleColors?.map((item) => {
              return {
                label: item.name,
                value: item.id,
              };
            })}
            searchOptions={{
              onChange: handleColorOnChange,
              placeholder: 'جستجو رنگ',
            }}
            rules={{
              required: PUBLIC_REQUIRED_RULE,
            }}
          />
          <TextInput
            name="vehicle_seat"
            label="تعداد سرنشین"
            placeholder="تعداد سرنشین را وارد کنید"
            rules={{
              required: PUBLIC_REQUIRED_RULE,
              pattern: {
                value: /^[0-9]+$/,
                message: 'تعداد سرنشین باید عدد باشد',
              },
            }}
          />
          <TextInput
            name="vehicle_cylinder"
            label=" تعداد سیلندر"
            placeholder="تعداد سیلندر را وارد کنید"
            rules={{ required: PUBLIC_REQUIRED_RULE }}
            noCharInput
          />
          <TextInput
            name="vehicle_vin"
            label="شماره VIN"
            placeholder="شماره VIN را وارد کنید"
            max={17}
            rules={{
              required: PUBLIC_REQUIRED_RULE,
              pattern: {
                value: /^[A-Z0-9]{17}$/,
                message:
                  'شماره VIN باید 17 کاراکتر و شامل حروف بزرگ انگلیسی و یا اعداد باشد',
              },
            }}
          />
          <TextInput
            name="vehicle_chassis"
            label={vehicleTargetPart}
            placeholder={`${vehicleTargetPart} را وارد کنید`}
            rules={{ required: PUBLIC_REQUIRED_RULE }}
          />
          <TextInput
            name="vehicle_engine"
            label="شماره موتور"
            placeholder="شماره موتور را وارد کنید"
            rules={{
              required: PUBLIC_REQUIRED_RULE,
              maxLength: {
                value: 50,
                message: 'شماره موتور باید 50 رقم باشد',
              },
            }}
          />
          <NumberInputWithWords
            name="vehicle_price"
            label="   ارزش روز وسیله‌نقلیه (ریال)"
            rules={{ required: PUBLIC_REQUIRED_RULE }}
          />
          <FlowButton
            disabled={!methods?.formState?.isValid}
            previousStepHandler={() =>
              navigate(INITIAL_VISIT_LINKS.VEHICLE_INFO(Number(id)!), {
                replace: true,
              })
            }
            text="ثبت"
            loading={isPending}
          />
        </Flex>
      </form>
    </FormProvider>
  );
};
export default InitialPartTwo;
